import { useQuery } from '@apollo/client'
import { useSelectedProjectElementNode } from 'components/WorkspaceEditor/Workspace2Store'
import { useParams } from 'react-router-dom'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { isOMBestPracticeFeatureEnabled } from 'shared/utils/bestPracticeUtils'
import { BestPracticeAuditHistoryQuery } from 'api/graphQL/queries/bestPractices.queries'
import {
    BestPracticeTip,
    BPTipAction,
    BPTipParameters,
    parameterConfig,
    ParameterKey,
} from '../types/bpManagement.types'

interface AuditConfigurationSnapshot {
    isGlobal: boolean
    projectGroup?: string | null
    owner?: string | null
    buildingType?: string | null
    sustainabilityType?: string | null
    contractType?: string | null
    constructionType?: string | null
    geographicRegion?: string | null
    seismicZone?: string | null
    windZone?: string | null
}

interface AuditUserProfile {
    firstName?: string | null
    lastName?: string | null
}

interface AuditUser {
    id?: string | null
    email?: string | null
    profileInfo?: AuditUserProfile | null
}

interface AuditEntry {
    id: string
    action: BPTipAction
    timestamp: string
    userId?: string | null
    user?: AuditUser | null
    configuration?: AuditConfigurationSnapshot | null
    changeDescription?: string | null
}

const buildAuthorName = (entry: AuditEntry): string => {
    const profile = entry.user?.profileInfo
    const first = profile?.firstName?.trim()
    const last = profile?.lastName?.trim()
    if (first || last) return [first, last].filter(Boolean).join(' ')
    if (entry.user?.email) return entry.user.email
    return entry.userId || 'Unknown user'
}

interface AuditHistoryResponse {
    getBestPracticeAuditHistory: AuditEntry[]
}

// Bridges the FE ParameterKey union with the BE AuditConfigurationSnapshot
// field names. Only `sustainability` ↔ `sustainabilityType` diverges; the
// other eight are identity. Centralising this here means future parameter
// additions only require adding to `parameterConfig` + this map.
const FE_TO_BE_PARAM_KEY: Record<
    ParameterKey,
    Exclude<keyof AuditConfigurationSnapshot, 'isGlobal'>
> = {
    projectGroup: 'projectGroup',
    owner: 'owner',
    buildingType: 'buildingType',
    constructionType: 'constructionType',
    contractType: 'contractType',
    sustainability: 'sustainabilityType',
    geographicRegion: 'geographicRegion',
    seismicZone: 'seismicZone',
    windZone: 'windZone',
}

const snapshotToParameters = (
    snapshot: AuditConfigurationSnapshot | null | undefined
): BPTipParameters | undefined => {
    if (!snapshot || snapshot.isGlobal) return undefined
    const params: BPTipParameters = {}
    parameterConfig.forEach(({ key }) => {
        const value = snapshot[FE_TO_BE_PARAM_KEY[key]]
        if (value) params[key] = value
    })
    return Object.keys(params).length > 0 ? params : undefined
}

const entryToTip = (entry: AuditEntry): BestPracticeTip => {
    const snapshot = entry.configuration
    const isGlobal = snapshot?.isGlobal ?? false
    return {
        id: entry.id,
        action: entry.action,
        type: isGlobal ? 'global' : 'parameter',
        author: { name: buildAuthorName(entry) },
        occurredAt: entry.timestamp,
        parameters: snapshotToParameters(snapshot),
        changeDescription: entry.changeDescription || undefined,
    }
}

interface UseBestPracticeAuditHistoryResult {
    tips: BestPracticeTip[]
    loading: boolean
    error: boolean
    /** True when no iterationId is available — the query can't fire. */
    waitingForIteration: boolean
}

/**
 * Fetches the BP audit history for the iteration the user is currently viewing
 * in the OM workspace. Gated behind the OM BP feature flag + OM workspace
 * check so the query never fires outside the supported context. Returns tips
 * newest-first as the BE returns them.
 */
export const useBestPracticeAuditHistory = (): UseBestPracticeAuditHistoryResult => {
    const { projectId } = useParams<{ projectId: string }>()
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
    const featureEnabled =
        isOMBestPracticeFeatureEnabled() && isCurrentlyOnOMWorkspace
    const iterationId = selectedProjectElementNode?.iteration?.id

    const { data, loading, error } = useQuery<AuditHistoryResponse>(
        BestPracticeAuditHistoryQuery,
        {
            variables: { iterationId },
            // `projectId` guards against firing outside a project route; the BE
            // resolves project context from iterationId (an iteration belongs to
            // exactly one project), so it is not passed as a query variable.
            skip: !featureEnabled || !iterationId || !projectId,
            fetchPolicy: 'cache-and-network',
        }
    )

    if (!featureEnabled) {
        return {
            tips: [],
            loading: false,
            error: false,
            waitingForIteration: false,
        }
    }

    return {
        tips: (data?.getBestPracticeAuditHistory ?? []).map(entryToTip),
        loading,
        error: Boolean(error),
        waitingForIteration: !iterationId,
    }
}
